/**
 * One-time token reveal — the post-create "copy it now" panel shared by
 * every surface that mints a secret token (#1573). Extracted verbatim
 * from the Account → API tokens page so the operator-token admin tab
 * reuses the exact same affordance instead of duplicating a11y-sensitive
 * code (copy button, focus-on-mount, the shown-once warning).
 *
 * Pins (unchanged from the original):
 *   - Icon-only Copy via the canonical `CopyIconButton` — Copy glyph,
 *     Check-on-success swap; tooltip "Copy to clipboard"; aria-label
 *     "Copy token to clipboard" (more descriptive than the tooltip
 *     because the surrounding chrome doesn't convey what's copied).
 *   - Dismiss receives focus on mount so keyboard users dismiss with
 *     Enter (their eye is already on the new token).
 *   - The plaintext renders into a readOnly Input that auto-selects on
 *     focus, a manual-copy fallback when the clipboard API is blocked.
 */
import { useEffect, useRef } from 'react'
import { KeyRound } from 'lucide-react'
import { Button, Input } from '@/components/ui'
import { CopyIconButton } from '@/components/CopyIconButton'

export function TokenReveal({
  token,
  onDismiss,
  /** Accessible label for the value input. Names what the token is for
   * (e.g. "New API token", "New operator token"). */
  label = 'New token',
}: {
  token: string
  onDismiss: () => void
  label?: string
}) {
  const dismissRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    dismissRef.current?.focus()
  }, [])

  return (
    <div className="rounded border border-success/40 bg-success/5 p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-success">
        <KeyRound className="h-4 w-4" aria-hidden="true" />
        Token created — copy it now
      </div>
      <p className="text-xs text-muted-foreground">
        This is the only time the full token is shown. Store it as a secret in your
        CI before dismissing.
      </p>
      <div className="flex items-center gap-2">
        <Input
          readOnly
          value={token}
          onFocus={(e) => e.currentTarget.select()}
          className="font-mono text-xs"
          aria-label={label}
        />
        <CopyIconButton
          value={token}
          title="Copy to clipboard"
          ariaLabel="Copy token to clipboard"
        />
      </div>
      <p className="text-xs text-warning">
        <span className="font-medium">Reminder:</span> we can't show this value
        again. If you lose it, you'll need to revoke and create a new one.
      </p>
      <div className="flex justify-end">
        <Button ref={dismissRef} onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </div>
  )
}
